// src/components/paginas/Usuarios.jsx
import React, { useEffect, useMemo, useState } from 'react';
import MostrarUsuario from '../ui/mostrarUsuario';

const API_BASE = process.env.REACT_APP_API_BASE;

// Map de roles a etiquetas legibles
const TIPO_LABEL = {
  adminGeneral: 'Administrador General',
  adminTec: 'Administrador Técnico',
  adminComercial: 'Administrador Comercial',
  bodeguero: 'Bodeguero',
  servicioCliente: 'Servicio al Cliente',
  despacho: 'Despacho de pedidos',
  particular: 'Cliente Particular',
  subDistribuidor: 'Sub Distribuidor',
  vendedor: 'Vendedor',
  mensajero: 'Mensajero',
};

const Tabs = ({ active, counts, onChange, loading }) => {
  const TabBtn = ({ id, label }) => {
    const isActive = active === id;
    return (
      <button
        onClick={() => onChange(id)}
        disabled={loading}
        className={[
          'px-4 py-2 rounded-full text-sm font-medium border transition',
          isActive
            ? 'bg-sky-600 text-white border-sky-600 shadow'
            : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50',
          'disabled:opacity-60'
        ].join(' ')}
      >
        {label}
        <span
          className={[
            'ml-2 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs border',
            isActive ? 'bg-white text-sky-700 border-white' : 'bg-slate-100 text-slate-600 border-slate-200'
          ].join(' ')}
        >
          {counts[id] ?? 0}
        </span>
      </button>
    );
  };

  return (
    <div className="flex items-center gap-2">
      <TabBtn id="clientes"  label="Clientes" />
      <TabBtn id="empleados" label="Empleados" />
      <TabBtn id="todos"     label="Todos" />
    </div>
  );
};

const Section = ({ title, count, children }) => (
  <section className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
    <div className="px-4 sm:px-5 py-3 border-b bg-slate-50/60 flex items-center justify-between">
      <h2 className="text-base sm:text-lg font-semibold text-slate-800">{title}</h2>
      <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-slate-600">
        {count}
      </span>
    </div>

    {/* Grid de cards (sin contenedores extra para evitar doble borde) */}
    <div className="p-3 sm:p-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 auto-rows-fr">
        {children}
      </div>
    </div>
  </section>
);

const UserSkeleton = () => (
  <div className="h-full min-h-[20rem] rounded-xl border border-slate-200 bg-white p-4 animate-pulse">
    <div className="flex justify-end">
      <div className="h-5 w-16 bg-slate-200 rounded-full" />
    </div>
    <div className="mt-6 flex justify-center">
      <div className="w-24 h-24 rounded-full bg-slate-200" />
    </div>
    <div className="mt-4 space-y-2 px-6">
      <div className="h-5 w-2/3 bg-slate-200 rounded" />
      <div className="h-4 w-1/2 bg-slate-200 rounded" />
      <div className="h-4 w-4/5 bg-slate-200 rounded" />
      <div className="h-4 w-3/5 bg-slate-200 rounded" />
    </div>
    <div className="mt-6 grid grid-cols-2 gap-2 px-6">
      <div className="h-9 w-full bg-slate-200 rounded" />
      <div className="h-9 w-full bg-slate-200 rounded" />
    </div>
  </div>
);

export default function Usuarios() {
  const [usuarios, setUsuarios]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [activeTab, setActiveTab] = useState('clientes'); // clientes | empleados | todos
  const [err, setErr]             = useState('');

  const fetchUsuarios = async () => {
    try {
      setLoading(true);
      setErr('');
      const response = await fetch(`${API_BASE}/api/usuarios`, { credentials: 'include' });
      if (!response.ok) throw new Error('No se pudieron obtener los usuarios');
      const data = await response.json();
      setUsuarios(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Error al obtener usuarios:', e);
      setErr(e.message || 'Error al cargar usuarios');
      setUsuarios([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsuarios(); }, []);

  // Conteos para tabs
  const counts = useMemo(() => {
    const c = { clientes: 0, empleados: 0, todos: usuarios.length };
    for (const u of usuarios) {
      if (u.tipoUsuario === 'particular' || u.tipoUsuario === 'subDistribuidor') c.clientes++;
      else c.empleados++;
    }
    return c;
  }, [usuarios]);

  // Filtro por pestaña
  const usuariosFiltrados = useMemo(() => {
    if (activeTab === 'clientes') {
      return usuarios.filter(u => u.tipoUsuario === 'particular' || u.tipoUsuario === 'subDistribuidor');
    }
    if (activeTab === 'empleados') {
      return usuarios.filter(u => !(u.tipoUsuario === 'particular' || u.tipoUsuario === 'subDistribuidor'));
    }
    return usuarios;
  }, [usuarios, activeTab]);

  // Agrupar por tipo
  const usuariosPorTipo = useMemo(() => {
    return usuariosFiltrados.reduce((acc, u) => {
      const t = u.tipoUsuario || 'otro';
      (acc[t] ||= []).push(u);
      return acc;
    }, {});
  }, [usuariosFiltrados]);

  const tipoKeys = Object.keys(usuariosPorTipo);

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900">Usuarios</h1>
          <p className="text-slate-600 mt-1">Gestión y visualización por tipo.</p>
        </div>
        <div className="flex items-center gap-2">
          <Tabs active={activeTab} counts={counts} onChange={setActiveTab} loading={loading} />
          <button
            onClick={fetchUsuarios}
            disabled={loading}
            className="px-3 py-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-60"
            title="Actualizar"
          >
            ↻
          </button>
        </div>
      </div>

      {err && (
        <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 text-rose-700 px-4 py-3">
          {err}
        </div>
      )}

      <div className="mt-6 space-y-6">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 auto-rows-fr">
            {Array.from({ length: 6 }).map((_, i) => <UserSkeleton key={i} />)}
          </div>
        ) : tipoKeys.length > 0 ? (
          tipoKeys.map((tipo) => (
            <Section
              key={tipo}
              title={TIPO_LABEL[tipo] || tipo}
              count={usuariosPorTipo[tipo].length}
            >
              {usuariosPorTipo[tipo].map((usuario) => (
                // ⚠️ SIN envoltorio extra: mostramos la card directamente para evitar “doble borde”
                <MostrarUsuario
                  key={usuario.id}
                  usuario={usuario}
                  onEstadoCambiado={fetchUsuarios}
                />
              ))}
            </Section>
          ))
        ) : (
          <div className="text-center text-slate-500 py-10">
            No hay usuarios registrados.
          </div>
        )}
      </div>
    </div>
  );
}
